import { Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef } from "@angular/core";
import { UntypedFormControl, ControlValueAccessor } from "@angular/forms";
import { add, isValid, parse, sub } from "date-fns";
import { defineLocale } from "ngx-bootstrap/chronos";
import { BsLocaleService, BsDatepickerConfig } from "ngx-bootstrap/datepicker";
import { esLocale } from "ngx-bootstrap/locale";

import { createCustomInputControlValueAccessor } from "@shared/Components/form-input.component";

@Component({
  selector: "app-date-input",
  templateUrl: "./date-input.component.html",
  providers: [createCustomInputControlValueAccessor(DateInputComponent)],
})
export class DateInputComponent implements OnInit, OnChanges, ControlValueAccessor {
  public dateControl: UntypedFormControl;

  // Used for range inputs in mobile
  public rangeStartDate: Date;
  public rangeEndDate: Date;

  @Input() public label: string = "Fecha";
  @Input() public placeholder: string = "Seleccione una fecha";
  @Input() public range: boolean = false;
  @Input() public maxDate: Date | "now";
  @Input() public minDate: Date | "now";
  @Input() public info?: string | TemplateRef<any>;

  @Input() public startDateName = "fechaDesde";
  @Input() public endDateName = "fechaHasta";

  @Input() public requiredMark: boolean;
  @Input() public showColonAfterLabel: boolean = true;

  @Input() public initializeWithValue: boolean = false;
  @Input() public initialValue: Date | [Date, Date] | string;

  public bsConfig: Partial<BsDatepickerConfig> = {
    adaptivePosition: true,
    showWeekNumbers: false,
    containerClass: "theme-dark-blue",
    dateInputFormat: "DD MMMM YYYY",
    isAnimated: true,
  };

  constructor(private localeService: BsLocaleService, private bsDatepickerConfig: BsDatepickerConfig) {
    defineLocale("es", esLocale);
    this.localeService.use("es");
    this.bsDatepickerConfig.dateInputFormat = "DD MMMM YYYY";
  }
  public ngOnChanges(changes: SimpleChanges): void {
    const maxDate = changes.maxDate?.currentValue;
    if (maxDate && maxDate === "now") {
      this.maxDate = new Date();
    }
    if (changes.minDate) {
      const minDate = changes.minDate.currentValue;
      if (minDate === "now") {
        this.minDate = new Date();
      }
    }
  }

  public ngOnInit(): void {
    this.dateControl = new UntypedFormControl();
    if (this.initializeWithValue && this.initialValue) {
      this.initializeDateControlValue();
    }
    if (!this.placeholder) {
      this.placeholder = "Seleccione una fecha";
    }
  }

  private initializeDateControlValue(): void {
    let valueToSet = this.initialValue;

    // Check if initialValue is a string and needs parsing
    if (typeof this.initialValue === "string") {
      valueToSet = new Date(this.initialValue);
    }
    // Alternatively, handle a range of dates
    else if (Array.isArray(this.initialValue) && this.initialValue.length === 2) {
      const start = new Date(this.initialValue[0]);
      const end = new Date(this.initialValue[1]);
      valueToSet = [start, end];
    }

    this.writeValue(valueToSet);
    this.dateControl.updateValueAndValidity();
  }

  public writeValue(value: Date | [Date, Date] | string): void {
    if (value && Array.isArray(value) && value.length === 2) {
      this.rangeStartDate = new Date(value[0]);
      this.rangeEndDate = new Date(value[1]);
      this.dateControl.setValue([this.rangeStartDate, this.rangeEndDate], { emitEvent: true });
    }
    if (value && value instanceof Date) {
      this.dateControl.setValue(value);
    }
  }

  public onUserKeyDown(keyEvent: KeyboardEvent): void {
    const input = keyEvent.target as HTMLInputElement;
    const value = input.value;
    const key = keyEvent.key;

    // Only allow numeric characters and backspace
    if (!/^\d$/.test(key) && key !== "Backspace") {
      keyEvent.preventDefault();
      return;
    }

    // Automatically insert "/" after the second and fourth characters
    if ((value.length === 2 || value.length === 5) && key !== "Backspace") {
      input.value += "/";
    }
  }

  public onUserType(inputEvent: InputEvent): void {
    const dateFormat = "dd/MM/yyyy";
    const value = (inputEvent as any).target.value;
    const [day, month, year] = value.split("/");
    const date = parse(`${day}/${month}/${year}`, dateFormat, new Date());
    const isValidDate = day?.length && month?.length && year?.length === 4 && isValid(date);

    if (isValidDate) {
      this.dateControl.setValue(date);
    }
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.dateControl.valueChanges.subscribe((value) => {
      if (this.range && Array.isArray(value)) {
        if (value[0] && value[1]) {
          const endDate = sub(add(this.removeTime(value[1]), { days: 1 }), { minutes: 1 });
          fn({
            [this.endDateName]: endDate,
            [this.startDateName]: this.removeTime(value[0]),
          });
        }
      } else {
        fn(this.removeTime(value));
      }
    });
  }

  public registerOnTouched(fn: () => void): void {
    this.dateControl.markAsTouched();
    fn();
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled
      ? this.dateControl.disable({ emitEvent: false, onlySelf: true })
      : this.dateControl.enable({ emitEvent: false, onlySelf: true });
  }

  public get id(): string {
    return this.label.split(" ").join("-").toLowerCase();
  }

  private removeTime(date: Date) {
    if (!date) {
      return null;
    }

    const dateString = date.toISOString().split("T")[0];
    return parse(dateString, "yyyy-MM-dd", new Date());
  }

  public onChangeStartDate(date: Date): void {
    this.rangeStartDate = date;
    this.updateControlValue();
  }

  public onChangeEndDate(date: Date): void {
    this.rangeEndDate = date;
    this.updateControlValue();
  }

  private updateControlValue(): void {
    if (this.rangeStartDate && this.rangeEndDate) {
      this.dateControl.setValue([this.rangeStartDate, this.rangeEndDate]);
    }
  }
}
